import { Spin } from "./Spin";
import { FloatingLayer } from "./FloatingLayer";
import React, { type CSSProperties, type ReactElement, type ReactNode } from "react";
import { autoUpdate, flip, offset, shift, size as sizeMiddleware, useDismiss, useFloating, useInteractions } from "@floating-ui/react";
import { CheckOutlined, CloseCircleFilled, CloseOutlined, DownOutlined, SearchOutlined } from "@ant-design/icons";
import { Empty } from "./Display";
import { cx, useKitDisabled, useKitFieldStatus, useKitSize } from "./fieldContext";
import { useUiText } from "./uiText";
import { KEY_ARROW_DOWN, KEY_ARROW_UP, KEY_BACKSPACE, KEY_ENTER, KEY_ESCAPE, KEY_TAB } from "./keyboardKeys";
const DEFAULT_LIST_HEIGHT = 256;
const FLOATING_OFFSET = 4;
const MIN_LIST_HEIGHT = 120;
const CHILDREN_FILTER_PROP = "children";
const TAG_ROW_RESERVED_WIDTH = 48;
const TAG_GAP = 4;

/*
 * Auswahl des Kits (Plan 2026-10-13-antd-ausbau, Stufe 6; Ersatz für Antd `Select` und `AutoComplete`).
 * Schnittstelle wie Antd im genutzten Umfang: Einzel-, Mehrfach- und Tag-Auswahl, Suche (Standard an, gefiltert
 * nach Beschriftung und Wert wie bisher über den `ConfigProvider`), eigene Filter, Gruppen, `labelInValue`,
 * `optionRender`/`labelRender`/`tagRender`, `maxTagCount`. Tastatur: Pfeile wählen vor, Enter übernimmt,
 * Escape schließt, Rücktaste entfernt bei leerer Suche den letzten Eintrag.
 */

/* Wie Antd sind Werte und Zusatzfelder der Optionen frei. */
/* eslint-disable @typescript-eslint/no-explicit-any */
export type SelectOption = {
  label?: ReactNode;
  value?: any;
  disabled?: boolean;
  title?: string;
  className?: string;
  style?: CSSProperties;
  key?: React.Key;
  children?: ReactNode;
  options?: SelectOption[];
  [field: string]: any;
};
export type DefaultOptionType = SelectOption;
export type LabeledValue = { value: any; label?: ReactNode; key?: React.Key };
type FlatOption = { option: SelectOption; group?: SelectOption; index: number };
/* eslint-enable @typescript-eslint/no-explicit-any */

export type SelectMode = "multiple" | "tags";
export type CustomTagProps = { label: ReactNode; value: unknown; disabled: boolean; closable: boolean; onClose: (event?: React.MouseEvent) => void; isMaxTag: boolean };

export interface SelectProps<V = any> {
  value?: V | null;
  defaultValue?: V | null;
  onChange?: (value: V, option?: any) => void;
  onSelect?: (value: any, option: SelectOption) => void;
  onDeselect?: (value: any, option?: SelectOption) => void;
  onClear?: () => void;
  options?: SelectOption[];
  children?: ReactNode;
  mode?: SelectMode;
  labelInValue?: boolean;
  placeholder?: ReactNode;
  allowClear?: boolean | { clearIcon?: ReactNode };
  disabled?: boolean;
  loading?: boolean;
  /** Suche; ohne Angabe an (frühere App-Vorgabe). Objektform wie Antd v6. */
  showSearch?: boolean | { optionFilterProp?: string | string[]; filterOption?: SelectProps["filterOption"]; searchValue?: string; onSearch?: (value: string) => void };
  optionFilterProp?: string | string[];
  filterOption?: boolean | ((input: string, option?: SelectOption) => boolean);
  searchValue?: string;
  onSearch?: (value: string) => void;
  autoClearSearchValue?: boolean;
  /** Einzelwahl: Tab übernimmt bei eingegebener Suche genau einen verfügbaren Treffer. */
  selectOnTab?: boolean;
  optionLabelProp?: string;
  optionRender?: (option: { label?: ReactNode; value?: unknown; key?: React.Key; data: SelectOption }, info: { index: number }) => ReactNode;
  labelRender?: (props: { label: ReactNode; value: unknown }) => ReactNode;
  tagRender?: (props: CustomTagProps) => ReactElement;
  maxTagCount?: number | "responsive";
  maxTagPlaceholder?: ReactNode | ((omitted: LabeledValue[]) => ReactNode);
  tokenSeparators?: string[];
  notFoundContent?: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Antd-Altname für `onOpenChange`. */
  onDropdownVisibleChange?: (open: boolean) => void;
  popupMatchSelectWidth?: boolean | number;
  /** Antd-Altname für `popupMatchSelectWidth`. */
  dropdownMatchSelectWidth?: boolean | number;
  listHeight?: number;
  /** Antd-Option ohne Wirkung im Kit (Liste mit eigenem Scrollen). */
  virtual?: boolean;
  dropdownStyle?: CSSProperties;
  popupClassName?: string;
  dropdownRender?: (menu: ReactNode) => ReactNode;
  popupRender?: (menu: ReactNode) => ReactNode;
  styles?: { popup?: { root?: CSSProperties } };
  getPopupContainer?: (node: HTMLElement) => HTMLElement;
  suffixIcon?: ReactNode;
  removeIcon?: ReactNode;
  menuItemSelectedIcon?: ReactNode;
  status?: "error" | "warning" | "";
  variant?: "outlined" | "filled" | "borderless" | "underlined";
  size?: "small" | "middle" | "large";
  className?: string;
  rootClassName?: string;
  style?: CSSProperties;
  id?: string;
  autoFocus?: boolean;
  tabIndex?: number;
  onBlur?: React.FocusEventHandler<HTMLElement>;
  onFocus?: React.FocusEventHandler<HTMLElement>;
  onInputKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onPopupScroll?: React.UIEventHandler<HTMLDivElement>;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
}

const DEFAULT_FILTER_PROPS = ["label", "value"];
/** Mindestabstand der Auswahlliste zum Fensterrand (px, wie Antd). */
const EDGE = 8;

function optionsFromChildren(children: ReactNode): SelectOption[] {
  const result: SelectOption[] = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    const props = child.props as Record<string, unknown>;
    if ((child.type as { isSelectOptGroup?: boolean }).isSelectOptGroup) {
      result.push({ label: props.label as ReactNode, key: child.key ?? undefined, options: optionsFromChildren(props.children as ReactNode) });
      return;
    }
    const value = props.value ?? child.key;
    result.push({ ...props, value, key: child.key ?? undefined, label: (props.label as ReactNode) ?? (props.children as ReactNode) });
  });
  return result;
}

function flattenOptions(options: SelectOption[]): FlatOption[] {
  const flat: FlatOption[] = [];
  for (const option of options) {
    if (Array.isArray(option.options)) {
      for (const child of option.options) flat.push({ option: child, group: option, index: flat.length });
    } else flat.push({ option, index: flat.length });
  }
  return flat;
}

function textOf(node: unknown): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (React.isValidElement(node)) return textOf((node.props as { children?: unknown }).children);
  return "";
}

const sameValue = (a: unknown, b: unknown) => a === b || (a != null && b != null && String(a) === String(b) && typeof a === typeof b);
const toList = (value: unknown): unknown[] => (value == null ? [] : Array.isArray(value) ? value : [value]);

/** Auswahlliste (Ersatz für Antd `Select`). */
function SelectBase<V = any>(props: SelectProps<V>) {
  const {
    value: valueProp, defaultValue, onChange, onSelect, onDeselect, onClear, options: optionsProp, children, mode, labelInValue,
    placeholder, allowClear, disabled, loading, showSearch: showSearchProp, optionFilterProp, filterOption, searchValue: searchValueProp,
    onSearch, autoClearSearchValue = true, selectOnTab = false, optionLabelProp, optionRender, labelRender, tagRender, maxTagCount, maxTagPlaceholder,
    tokenSeparators, notFoundContent, open: openProp, defaultOpen, onOpenChange, onDropdownVisibleChange, popupMatchSelectWidth,
    dropdownMatchSelectWidth, listHeight = DEFAULT_LIST_HEIGHT, dropdownStyle, popupClassName, dropdownRender, popupRender, styles, getPopupContainer,
    suffixIcon, removeIcon, menuItemSelectedIcon, status, variant, size, className, rootClassName, style, id, autoFocus, tabIndex,
    onBlur, onFocus, onInputKeyDown, onPopupScroll, ...aria
  } = props;
  const { text } = useUiText();
  const listboxId = React.useId();
  const mergedDisabled = useKitDisabled(disabled);
  const mergedSize = useKitSize(size);
  const mergedStatus = useKitFieldStatus(status);
  const multiple = mode === "multiple" || mode === "tags";
  const searchConfig = typeof showSearchProp === "object" ? showSearchProp : undefined;
  const searchable = showSearchProp !== false || mode === "tags";
  const filterProps = toList(searchConfig?.optionFilterProp ?? optionFilterProp ?? DEFAULT_FILTER_PROPS).map(String);
  const filter = searchConfig?.filterOption ?? filterOption;
  const handleSearch = searchConfig?.onSearch ?? onSearch;

  const allOptions = React.useMemo(() => optionsProp ?? optionsFromChildren(children), [children, optionsProp]);
  const flatAll = React.useMemo(() => flattenOptions(allOptions), [allOptions]);

  const [innerValue, setInnerValue] = React.useState<unknown>(defaultValue);
  const rawValue = valueProp !== undefined ? valueProp : innerValue;
  const values = toList(rawValue).map((item) => (labelInValue && item && typeof item === "object" ? (item as LabeledValue).value : item));

  const [innerSearch, setInnerSearch] = React.useState("");
  const search = searchConfig?.searchValue ?? searchValueProp ?? innerSearch;
  const setSearch = (next: string) => {
    if (searchValueProp === undefined && searchConfig?.searchValue === undefined) setInnerSearch(next);
    handleSearch?.(next);
  };

  const [innerOpen, setInnerOpen] = React.useState(Boolean(defaultOpen));
  const open = !mergedDisabled && (openProp ?? innerOpen);
  const setOpen = (next: boolean) => {
    if (next === open) return;
    if (openProp === undefined) setInnerOpen(next);
    onOpenChange?.(next);
    onDropdownVisibleChange?.(next);
    if (!next && autoClearSearchValue && search) setSearch("");
  };

  const matchWidth = popupMatchSelectWidth ?? dropdownMatchSelectWidth ?? true;
  const floating = useFloating({
    open,
    onOpenChange: (next) => setOpen(next),
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(FLOATING_OFFSET),
      flip({ padding: EDGE }),
      shift({ padding: EDGE }),
      sizeMiddleware({
        padding: EDGE,
        apply({ rects, elements, availableHeight }) {
          const width = typeof matchWidth === "number" ? `${matchWidth}px` : `${rects.reference.width}px`;
          Object.assign(elements.floating.style, matchWidth === false ? { minWidth: width } : { width }, { maxHeight: `${Math.max(MIN_LIST_HEIGHT, availableHeight)}px` });
        },
      }),
    ],
  });
  const dismiss = useDismiss(floating.context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  const findOption = (value: unknown) => flatAll.find((entry) => sameValue(entry.option.value, value))?.option;
  const labelFor = (value: unknown): ReactNode => {
    if (labelInValue) {
      const raw = toList(rawValue).find((item) => item && typeof item === "object" && sameValue((item as LabeledValue).value, value)) as LabeledValue | undefined;
      if (raw?.label !== undefined) return raw.label;
    }
    const option = findOption(value);
    if (!option) return value as ReactNode;
    return (optionLabelProp ? (option[optionLabelProp] as ReactNode) : undefined) ?? option.label ?? option.children ?? (option.value as ReactNode);
  };

  const filtered = React.useMemo(() => {
    const query = search.trim();
    if (!searchable || !query || filter === false) return flatAll;
    const lower = query.toLowerCase();
    const matches = (option: SelectOption) => {
      if (typeof filter === "function") return filter(query, option);
      return filterProps.some((prop) => textOf(prop === CHILDREN_FILTER_PROP ? option.children ?? option.label : option[prop]).toLowerCase().includes(lower));
    };
    return flatAll.filter((entry) => matches(entry.option));
  }, [filter, filterProps, flatAll, search, searchable]);

  const tagCandidate = mode === "tags" && search.trim() && !flatAll.some((entry) => sameValue(entry.option.value, search.trim())) ? search.trim() : null;
  const visible: FlatOption[] = tagCandidate ? [{ option: { value: tagCandidate, label: tagCandidate }, index: -1 }, ...filtered] : filtered;
  const [active, setActive] = React.useState(0);
  const activeIndex = Math.min(active, Math.max(0, visible.length - 1));

  const emit = (nextValues: unknown[]) => {
    const toOutput = (value: unknown) => (labelInValue ? { value, label: labelFor(value), key: String(value) } : value);
    const output = multiple ? nextValues.map(toOutput) : nextValues.length ? toOutput(nextValues[0]) : undefined;
    const optionOut = multiple ? nextValues.map((value) => findOption(value) ?? { value, label: value as ReactNode }) : nextValues.length ? findOption(nextValues[0]) : undefined;
    if (valueProp === undefined) setInnerValue(output);
    onChange?.(output as V, optionOut);
  };

  const choose = (option: SelectOption) => {
    if (option.disabled) return;
    const value = option.value;
    if (multiple) {
      const has = values.some((item) => sameValue(item, value));
      if (has) { emit(values.filter((item) => !sameValue(item, value))); onDeselect?.(value, option); }
      else { emit([...values, value]); onSelect?.(value, option); }
      if (autoClearSearchValue && search) setSearch("");
      inputRef.current?.focus();
      return;
    }
    if (!values.some((item) => sameValue(item, value))) emit([value]);
    onSelect?.(value, option);
    setOpen(false);
    if (search) setSearch("");
  };

  const removeValue = (value: unknown) => {
    emit(values.filter((item) => !sameValue(item, value)));
    onDeselect?.(value, findOption(value));
  };

  const clear = (event: React.MouseEvent) => {
    event.stopPropagation();
    emit([]);
    if (search) setSearch("");
    onClear?.();
  };

  const onSearchInput = (next: string) => {
    if (mode === "tags" && tokenSeparators?.length && tokenSeparators.some((token) => next.includes(token))) {
      const parts = next.split(new RegExp(tokenSeparators.map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|"))).map((part) => part.trim()).filter(Boolean);
      const merged = [...values];
      for (const part of parts) if (!merged.some((item) => sameValue(item, part))) merged.push(part);
      emit(merged);
      setSearch("");
      return;
    }
    setSearch(next);
    setActive(0);
    if (!open) setOpen(true);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onInputKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (event.key === KEY_ARROW_DOWN || event.key === KEY_ARROW_UP) {
      event.preventDefault();
      if (!open) { setOpen(true); return; }
      const step = event.key === KEY_ARROW_DOWN ? 1 : -1;
      let next = activeIndex;
      for (let i = 0; i < visible.length; i++) {
        next = (next + step + visible.length) % visible.length;
        if (!visible[next].option.disabled) break;
      }
      setActive(next);
      listRef.current?.querySelector<HTMLElement>(`[data-index="${next}"]`)?.scrollIntoView({ block: "nearest" });
    } else if (event.key === KEY_ENTER) {
      if (!open) { event.preventDefault(); setOpen(true); return; }
      const entry = visible[activeIndex];
      if (entry) { event.preventDefault(); choose(entry.option); }
    } else if (event.key === KEY_ESCAPE) {
      if (open) { event.preventDefault(); event.stopPropagation(); setOpen(false); }
    } else if (event.key === KEY_BACKSPACE && multiple && !search && values.length) {
      removeValue(values[values.length - 1]);
    } else if (event.key === KEY_TAB) {
      if (selectOnTab && !multiple && open && search.trim() && !loading && !event.nativeEvent.isComposing) {
        const candidates = visible.filter(entry => !entry.option.disabled);
        if (candidates.length === 1) { choose(candidates[0].option); return; }
      }
      setOpen(false);
    }
  };

  // ---------------------------------------------------------------- Anzeige der Auswahl
  // `maxTagCount="responsive"`: so viele Einträge wie in eine Zeile passen, der Rest als „+ n …“.
  const overflowRef = React.useRef<HTMLDivElement>(null);
  const [fitCount, setFitCount] = React.useState<number | null>(null);
  const responsiveTags = maxTagCount === "responsive";
  const valueKey = values.map(String).join("|");
  React.useLayoutEffect(() => {
    const container = overflowRef.current;
    if (!responsiveTags || !container) return;
    const measure = () => {
      const available = container.clientWidth - TAG_ROW_RESERVED_WIDTH;
      const tags = Array.from(container.querySelectorAll<HTMLElement>(":scope > .ck-select-selection-measure > .ck-select-selection-item"));
      let used = 0;
      let count = 0;
      for (const tag of tags) {
        used += tag.offsetWidth + TAG_GAP;
        if (used > available) break;
        count++;
      }
      const next = count >= tags.length ? null : Math.max(1, count);
      setFitCount((previous) => (previous === next ? previous : next));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [responsiveTags, valueKey]);
  const tagLimit = typeof maxTagCount === "number" ? maxTagCount : responsiveTags ? fitCount ?? undefined : undefined;
  const omitted = tagLimit !== undefined && values.length > tagLimit ? values.slice(tagLimit) : [];
  const shownValues = tagLimit !== undefined ? values.slice(0, tagLimit) : values;
  const renderTag = (value: unknown, isMaxTag = false) => {
    const label = labelFor(value);
    const option = findOption(value);
    const closable = !mergedDisabled && !option?.disabled;
    const onClose = (event?: React.MouseEvent) => { event?.stopPropagation(); removeValue(value); };
    if (tagRender) return <React.Fragment key={String(value)}>{tagRender({ label, value, disabled: Boolean(option?.disabled), closable, onClose, isMaxTag })}</React.Fragment>;
    return (
      <span key={String(value)} className="ck-select-selection-item" title={typeof label === "string" ? label : undefined}>
        <span className="ck-select-selection-item-content">{label}</span>
        {closable ? (
          <span className="ck-select-selection-item-remove" role="button" aria-label={text("Remove")} onMouseDown={(event) => event.preventDefault()} onClick={onClose}>
            {removeIcon ?? <CloseOutlined />}
          </span>
        ) : null}
      </span>
    );
  };
  const omittedNode = omitted.length ? (
    <span className="ck-select-selection-item ck-select-selection-item-rest">
      <span className="ck-select-selection-item-content">
        {typeof maxTagPlaceholder === "function" ? maxTagPlaceholder(omitted.map((value) => ({ value, label: labelFor(value) }))) : maxTagPlaceholder ?? `+ ${omitted.length} …`}
      </span>
    </span>
  ) : null;

  const singleLabel = !multiple && values.length ? labelFor(values[0]) : null;
  const showPlaceholder = values.length === 0 && !search;
  const showClear = Boolean(allowClear) && !mergedDisabled && (values.length > 0 || Boolean(search));

  const selector = (
    <div
      ref={floating.refs.setReference}
      className={cx(
        "ck-field", "ck-field--affix", "ck-select", multiple ? "ck-select-multiple" : "ck-select-single",
        open && "ck-select-open", mergedDisabled && "ck-select-disabled is-disabled", searchable && "ck-select-show-search",
        mergedSize === "small" && "ck-field--sm", mergedSize === "large" && "ck-field--lg", variant && `ck-field--${variant}`,
        mergedStatus && `is-${mergedStatus}`, responsiveTags && "ck-select-responsive", className, rootClassName,
      )}
      style={style}
      {...getReferenceProps({
        onMouseDown: (event: React.MouseEvent) => {
          if (mergedDisabled) return;
          if (event.target !== inputRef.current) event.preventDefault();
          inputRef.current?.focus();
          setOpen(!open || multiple);
        },
      })}
    >
      <div className="ck-select-selector">
        {multiple ? (
          <div ref={overflowRef} className="ck-select-selection-overflow">
            {responsiveTags ? <span className="ck-select-selection-measure" aria-hidden="true">{values.map((value) => renderTag(value))}</span> : null}
            {shownValues.map((value) => renderTag(value))}
            {omittedNode}
            <span className="ck-select-selection-search">{searchInput()}</span>
          </div>
        ) : (
          <>
            <span className="ck-select-selection-search">{searchInput()}</span>
            {singleLabel !== null && !search ? (
              <span className="ck-select-selection-item" title={typeof singleLabel === "string" ? singleLabel : undefined}>
                {labelRender ? labelRender({ label: singleLabel, value: values[0] }) : singleLabel}
              </span>
            ) : null}
          </>
        )}
        {showPlaceholder && placeholder != null ? <span className="ck-select-selection-placeholder">{placeholder}</span> : null}
      </div>
      {showClear ? (
        <span className="ck-select-clear" role="button" aria-label={text("Clear")} onMouseDown={(event) => event.preventDefault()} onClick={clear}>
          {typeof allowClear === "object" && allowClear.clearIcon ? allowClear.clearIcon : <CloseCircleFilled />}
        </span>
      ) : null}
      <span className={cx("ck-select-arrow", showClear && "has-clear")} aria-hidden="true">
        {loading ? <Spin inline /> : suffixIcon !== undefined ? suffixIcon : open && searchable ? <SearchOutlined /> : <DownOutlined />}
      </span>
    </div>
  );

  function searchInput() {
    return (
      <input
        ref={inputRef}
        id={id}
        className="ck-select-selection-search-input"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={open && visible[activeIndex] ? `${listboxId}-${activeIndex}` : undefined}
        aria-invalid={mergedStatus === "error" || undefined}
        autoComplete="off"
        autoFocus={autoFocus}
        tabIndex={tabIndex}
        disabled={mergedDisabled}
        readOnly={!searchable}
        value={search}
        size={1}
        {...aria}
        onChange={(event) => onSearchInput(event.target.value)}
        onKeyDown={onKeyDown}
        onFocus={(event) => onFocus?.(event)}
        onBlur={(event) => {
          if (listRef.current?.contains(event.relatedTarget as Node | null)) return;
          setOpen(false);
          onBlur?.(event);
        }}
      />
    );
  }

  // ---------------------------------------------------------------- Liste
  let lastGroup: SelectOption | undefined;
  const list = visible.length ? (
    <div ref={listRef} id={listboxId} role="listbox" aria-multiselectable={multiple || undefined} className="ck-select-item-list" style={{ maxHeight: listHeight }} onScroll={onPopupScroll}>
      {visible.map((entry, position) => {
        const { option } = entry;
        const selected = values.some((item) => sameValue(item, option.value));
        const groupHeader = entry.group && entry.group !== lastGroup ? (
          <div key={`group-${String(entry.group.key ?? entry.group.label)}`} className="ck-select-item-group" role="presentation">{entry.group.label}</div>
        ) : null;
        lastGroup = entry.group;
        const content = optionRender ? optionRender({ label: option.label, value: option.value, key: option.key, data: option }, { index: position }) : option.label ?? option.children ?? String(option.value);
        return (
          <React.Fragment key={`${String(option.value)}-${position}`}>
            {groupHeader}
            <div
              id={`${listboxId}-${position}`}
              data-index={position}
              role="option"
              aria-selected={selected}
              aria-disabled={option.disabled || undefined}
              title={option.title ?? (typeof option.label === "string" ? option.label : undefined)}
              className={cx(
                "ck-select-item", "ck-select-item-option", entry.group && "ck-select-item-option-grouped",
                selected && "ck-select-item-option-selected", position === activeIndex && "ck-select-item-option-active",
                option.disabled && "ck-select-item-option-disabled", option.className,
              )}
              style={option.style}
              onMouseEnter={() => setActive(position)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(option)}
            >
              <div className="ck-select-item-option-content">{content}</div>
              {multiple && selected ? <span className="ck-select-item-option-state" aria-hidden="true">{menuItemSelectedIcon ?? <CheckOutlined />}</span> : null}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  ) : (
    <div className="ck-select-empty">{notFoundContent !== undefined ? notFoundContent : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={text("No data")} />}</div>
  );
  const render = popupRender ?? dropdownRender;
  const portalRoot = getPopupContainer && floating.elements.domReference instanceof HTMLElement ? getPopupContainer(floating.elements.domReference) : undefined;

  return (
    <>
      {selector}
      {open ? (
        <FloatingLayer root={portalRoot}>
          <div
            ref={floating.refs.setFloating}
            className={cx("ck-select-dropdown", multiple && "ck-select-dropdown-multiple", popupClassName)}
            style={{ ...floating.floatingStyles, zIndex: 1050, ...dropdownStyle, ...styles?.popup?.root }}
            {...getFloatingProps({ onMouseDown: (event: React.MouseEvent) => event.preventDefault() })}
          >
            {render ? render(list) : list}
          </div>
        </FloatingLayer>
      ) : null}
    </>
  );
}

/* Antd-Kompatibilität für `<Select.Option>`/`<Select.OptGroup>` als Kinder; gerendert wird über `options`. */
function SelectOptionElement(_props: SelectOption & { children?: ReactNode }): null { return null; }
function SelectOptGroupElement(_props: { label?: ReactNode; children?: ReactNode; key?: React.Key }): null { return null; }
SelectOptGroupElement.isSelectOptGroup = true;

export const Select = Object.assign(SelectBase as <V = any>(props: SelectProps<V>) => ReactElement, {
  Option: SelectOptionElement,
  OptGroup: SelectOptGroupElement,
});

// ---------------------------------------------------------------- AutoComplete

export interface AutoCompleteProps {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onSelect?: (value: string, option: SelectOption) => void;
  onSearch?: (value: string) => void;
  onClear?: () => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  options?: SelectOption[];
  filterOption?: boolean | ((input: string, option?: SelectOption) => boolean);
  placeholder?: string;
  allowClear?: boolean;
  disabled?: boolean;
  notFoundContent?: ReactNode;
  size?: "small" | "middle" | "large";
  status?: "error" | "warning" | "";
  maxLength?: number;
  className?: string;
  style?: CSSProperties;
  id?: string;
  "aria-label"?: string;
}

/** Freitext mit Vorschlägen (Ersatz für Antd `AutoComplete`): die Eingabe ist der Wert, Vorschläge ergänzen. */
export function AutoComplete({ value, defaultValue, onChange, onSelect, onSearch, onClear, onFocus, onBlur, options = [], filterOption = true, placeholder, allowClear, disabled, notFoundContent, size, status, maxLength, className, style, id, ...aria }: AutoCompleteProps) {
  const { text } = useUiText();
  const listboxId = React.useId();
  const mergedDisabled = useKitDisabled(disabled);
  const mergedSize = useKitSize(size);
  const mergedStatus = useKitFieldStatus(status);
  const [inner, setInner] = React.useState(defaultValue ?? "");
  const current = value ?? inner;
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(-1);
  const floating = useFloating({
    open, onOpenChange: setOpen, placement: "bottom-start", whileElementsMounted: autoUpdate,
    middleware: [offset(FLOATING_OFFSET), flip({ padding: EDGE }), shift({ padding: EDGE }), sizeMiddleware({ apply({ rects, elements }) { elements.floating.style.width = `${rects.reference.width}px`; } })],
  });
  const dismiss = useDismiss(floating.context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);
  const shown = filterOption === false ? options : options.filter((option) => typeof filterOption === "function"
    ? filterOption(current, option)
    : textOf(option.label ?? option.value).toLowerCase().includes(current.toLowerCase()));
  const set = (next: string) => {
    if (value === undefined) setInner(next);
    onChange?.(next);
  };
  const pick = (option: SelectOption) => {
    const next = String(option.value ?? "");
    set(next);
    onSelect?.(next, option);
    setOpen(false);
  };
  const showClear = allowClear && !mergedDisabled && current.length > 0;
  return (
    <>
      <span
        ref={floating.refs.setReference}
        className={cx("ck-field", "ck-field--affix", "ck-select", "ck-select-auto-complete", mergedSize === "small" && "ck-field--sm", mergedSize === "large" && "ck-field--lg", mergedStatus && `is-${mergedStatus}`, mergedDisabled && "is-disabled", className)}
        style={style}
        {...getReferenceProps()}
      >
        <input
          id={id}
          className="ck-field-input"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={open && active >= 0 ? `${listboxId}-${active}` : undefined}
          autoComplete="off"
          placeholder={placeholder}
          disabled={mergedDisabled}
          maxLength={maxLength}
          value={current}
          {...aria}
          onChange={(event) => { set(event.target.value); onSearch?.(event.target.value); setOpen(true); setActive(-1); }}
          onFocus={(event) => { setOpen(true); onFocus?.(event); }}
          onBlur={(event) => { setOpen(false); onBlur?.(event); }}
          onKeyDown={(event) => {
            if (event.key === KEY_ARROW_DOWN || event.key === KEY_ARROW_UP) {
              event.preventDefault();
              setOpen(true);
              if (shown.length) setActive((index) => (index + (event.key === KEY_ARROW_DOWN ? 1 : -1) + shown.length) % shown.length);
            } else if (event.key === KEY_ENTER && open && shown[active]) {
              event.preventDefault();
              pick(shown[active]);
            } else if (event.key === KEY_ESCAPE && open) {
              event.preventDefault();
              event.stopPropagation();
              setOpen(false);
            }
          }}
        />
        {showClear ? (
          <button type="button" tabIndex={-1} className="ck-field-clear" aria-label={text("Clear")} onMouseDown={(event) => event.preventDefault()} onClick={() => { set(""); return onClear?.(); }}>
            <CloseCircleFilled />
          </button>
        ) : null}
      </span>
      {open && (shown.length > 0 || notFoundContent != null) ? (
        <FloatingLayer>
          <div ref={floating.refs.setFloating} className="ck-select-dropdown" style={{ ...floating.floatingStyles, zIndex: 1050 }} {...getFloatingProps({ onMouseDown: (event: React.MouseEvent) => event.preventDefault() })}>
            {shown.length ? (
              <div id={listboxId} role="listbox" className="ck-select-item-list">
                {shown.map((option, index) => (
                  <div
                    key={`${String(option.value)}-${index}`}
                    id={`${listboxId}-${index}`}
                    role="option"
                    aria-selected={index === active}
                    className={cx("ck-select-item", "ck-select-item-option", index === active && "ck-select-item-option-active", option.disabled && "ck-select-item-option-disabled")}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => { if (!option.disabled) pick(option); }}
                  >
                    <div className="ck-select-item-option-content">{option.label ?? String(option.value)}</div>
                  </div>
                ))}
              </div>
            ) : <div className="ck-select-empty">{notFoundContent}</div>}
          </div>
        </FloatingLayer>
      ) : null}
    </>
  );
}
